import PropTypes from "prop-types";
import React from "react";

import { classNames } from "@swa-ui/string";

import { Background, backgroundPropTypes } from "../Background";
import styles from "./Badge.module.scss";

const HEIGHT_TO_SIZE_MAP = {
  large: "var(--cmp-core-badge-large-height-units-rem)",
  medium: "var(--cmp-core-badge-medium-height-units-rem)",
  small: "var(--cmp-core-badge-small-height-units-rem)",
  xlarge: "var(--cmp-core-badge-xlarge-height-units-rem)",
  xsmall: "var(--cmp-core-badge-xsmall-height-units-rem)",
};
const HEIGHTS_LINE = "2px";
const HEIGHTS_LINE_HEIGHT = "2rem";
const STYLE_TYPE_TO_BACKGROUND_MAP = {
  caution: "secondary",
  disabled: "neutral",
  error: "danger",
  information: "primary",
};
const STYLE_TYPE_TO_COLOR_MAP = {
  black: "neutral-100",
  caution: "secondary-500",
  danger: "tertiary-500",
  disabled: "neutral-800",
  error: "tertiary-500",
  information: "primary-600",
  "information-foreground": "primary-200",
  neutral: "neutral-200",
  primary: "primary-500",
  secondary: "neutral-500",
  success: "success-500",
  tertiary: "neutral-700",
  transparent: "transparent",
  warning: "secondary-500",
  white: "neutral-900",
};

/**
 * Badge renders a variety of small illustrations with labels- usually some sort of background with
 * optionally a small amount of text or JSX.
 */
export const Badge = (props) => {
  const {
    border,
    center,
    children,
    className,
    flagCutoutColor = "white",
    foregroundColor,
    fullWidth = false,
    height,
    padding = "small",
    styleType = "primary",
    type,
  } = props;

  return type === "flag" ? (
    renderFlag()
  ) : (
    <Background {...getProps()} style={getForegroundStyle()}>
      {children}
    </Background>
  );

  function renderFlag() {
    return (
      <div {...getFlagProps()}>
        <div className={getFlagChildrenClass()}>{children}</div>
        <div {...getFlagCutoutProps()} />
      </div>
    );
  }

  function getFlagProps() {
    return {
      className: getFlagClass(),
      style: {
        backgroundColor: `var(--${STYLE_TYPE_TO_COLOR_MAP[styleType] || styleType})`,
        height: getSize(),
      },
    };
  }

  function getFlagCutoutProps() {
    return {
      className: styles.flagCutout,
      style: {
        "--flag-background-color": `var(--${
          STYLE_TYPE_TO_COLOR_MAP[flagCutoutColor] || flagCutoutColor
        })`,
        "--size": getSize(),
      },
    };
  }

  function getProps() {
    return {
      border,
      className: getClass(),
      color: STYLE_TYPE_TO_BACKGROUND_MAP[styleType] || styleType,
      height,
      shape: type,
    };
  }

  function getForegroundStyle() {
    return foregroundColor
      ? { color: `var(--${STYLE_TYPE_TO_COLOR_MAP[foregroundColor] || foregroundColor})` }
      : undefined;
  }

  function getFlagClass() {
    return classNames(className, styles.badge);
  }

  function getFlagChildrenClass() {
    return classNames(styles.flagChildren, {
      [styles.paddingLarge]: padding === "large",
      [styles.paddingMedium]: padding === "medium",
      [styles.paddingSmall]: padding === "small",
      [styles.paddingXlarge]: padding === "xlarge",
      [styles.paddingXsmall]: padding === "xsmall",
    });
  }

  function getClass() {
    return classNames(className, styles.badge, {
      [styles.fullWidth]: fullWidth,
      [styles.leftJustify]: !center,
      [styles.paddingLarge]: padding === "large",
      [styles.paddingMedium]: padding === "medium",
      [styles.paddingSmall]: padding === "small",
      [styles.paddingXlarge]: padding === "xlarge",
      [styles.paddingXsmall]: padding === "xsmall",
    });
  }

  function getSize() {
    const heights = {
      line: HEIGHTS_LINE,
      lineHeight: HEIGHTS_LINE_HEIGHT,
    };

    return heights[height] || (height && HEIGHT_TO_SIZE_MAP[height]) || HEIGHT_TO_SIZE_MAP.medium;
  }
};

Badge.displayName = "Badge";

Badge.propTypes = {
  /** Optional border to surround shape. Cannot be given for Flag at this time. */
  border: backgroundPropTypes.border,

  /** Content will be centered if true. */
  center: PropTypes.bool,

  /** Content that will be rendered on the in the badge. */
  children: PropTypes.node,

  /**
   * Additional classes for positioning the component. Given classes may only position this component
   * for layout purposes, and cannot change how the component renders in any way.
   */
  className: PropTypes.string,

  /**
   * Color of background where a flag will be displayed. This prop is only need for the flag shape
   * and when the background is not white.
   * This prop is temporary and will be refactored at some future date.
   */
  flagCutoutColor: backgroundPropTypes.color,

  /** Foreground color that takes priority over styleType for the badge color. */
  foregroundColor: backgroundPropTypes.color,

  /** Option will use the full width of its container. */
  fullWidth: PropTypes.bool,

  /** Height as defined by Shape component. See that component for values. */
  height: backgroundPropTypes.height,

  /** Padding allows the caller to specify how much padding will be defined. */
  padding: PropTypes.oneOf(["large", "medium", "none", "small", "xlarge", "xsmall"]),

  /** Provides the variations in [background] treatment for the given badge type. */
  styleType: backgroundPropTypes.color,

  /** Defines which type of badge should be rendered. */
  type: PropTypes.oneOf([
    "capsule",
    "circle",
    "ellipse",
    "flag",
    "rectangle",
    "roundedRectangle",
    "square",
  ]),
};
